import { CircleEllipsis, PencilLineIcon } from 'lucide-react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuestionMarkedFill, useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedFillProps } from '.';

import { QuestionMarkedFillContent, QuestionMarkedFillForm } from '.';

export function QuestionMarkedFill(questionMarkedFillProps: QuestionMarkedFillProps) {
  const { marked, question, showMarked, showQuestion, answer, onEditMark } = questionMarkedFillProps;

  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();

  const { data, score, totalPoints, correct, isMarked, correctState, questionObj, getAnswerContent } = useQuestionMarkedFill(marked, question, answer);
  if (!showMarked) return null;

  if (!marked || !isMarked) return <QuestionMarkedNoAnswer showMarked={showMarked} />;

  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={totalPoints}
        correct={correct}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content}>
        <QuestionMarkedFillContent question={question} />
      </QuestionMarkedQuestionContent>
      {!openForm && (
        <QuestionMarkedUserAnswer>
          <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
          <div className="flex flex-wrap items-center gap-1">
            {data.map((content, index) => {
              const [gapContent, variant, explanation] = getAnswerContent(content.content, content.gap);
              if (content.gap) {
                return (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <Badge variant={variant} size="sm" className="h-fit text-wrap whitespace-normal">
                        {gapContent}
                        <CircleEllipsis />
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs whitespace-pre-wrap">
                      <span className="font-semibold">Wyjaśnienie:</span>
                      <p>{explanation ?? 'Brak'}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return <p key={index}>{gapContent}</p>;
            })}
          </div>
        </QuestionMarkedUserAnswer>
      )}
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedFillForm onSubmit={onEditMark} onClose={closeFormHandler} marked={questionObj.getMarked()} content={questionObj.getContent()} isOpen={openForm} />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
